const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'SPAN', 'BR', 'P', 'DIV', 'UL', 'OL', 'LI']);
const DROP_TAGS = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'LINK', 'META']);
const ENCODED_ALLOWED_TAG_PATTERN = /&(lt|#60);\/?(b|strong|i|em|u|span|br|p|div|ul|ol|li|font)\b/i;
const LEGACY_FONT_SIZE_MAP = {
  1: '10px',
  2: '13px',
  3: '16px',
  4: '18px',
  5: '24px',
  6: '32px',
  7: '48px',
};

function canUseDom() {
  return typeof window !== 'undefined' && typeof DOMParser !== 'undefined';
}

function collapseRepeatedEntityEncoding(value = '') {
  let normalized = String(value);
  let previous = '';

  while (normalized !== previous) {
    previous = normalized;
    normalized = normalized.replace(
      /&(?:(?:amp;)+)(nbsp|#160|#xa0|lt|#60|gt|#62|quot|#34|apos|#39);/gi,
      '&$1;'
    );
  }

  return normalized;
}

function decodeEncodedMarkup(value = '') {
  let normalized = collapseRepeatedEntityEncoding(value);

  if (!ENCODED_ALLOWED_TAG_PATTERN.test(normalized)) {
    return normalized;
  }

  let previous = '';
  let iterations = 0;

  while (normalized !== previous && iterations < 5) {
    previous = normalized;
    normalized = normalized
      .replace(/&(lt|#60);/gi, '<')
      .replace(/&(gt|#62);/gi, '>')
      .replace(/&(quot|#34);/gi, '"')
      .replace(/&(apos|#39);/gi, "'");
    normalized = collapseRepeatedEntityEncoding(normalized);
    iterations += 1;
  }

  return normalized;
}

function normalizeRichTextEntities(value = '') {
  return decodeEncodedMarkup(collapseRepeatedEntityEncoding(value));
}

function normalizeLineBreaks(value = '') {
  return normalizeRichTextEntities(value).replace(/\r\n?/g, '\n');
}

function escapeHtml(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function containsHtml(value = '') {
  return /<\/?[a-z][\s\S]*>|&(nbsp|#160|#xa0);/i.test(String(value));
}

function plainTextToRichText(value = '') {
  const normalized = normalizeLineBreaks(value).trim();

  if (!normalized) {
    return '';
  }

  return normalized
    .split(/\n{2,}/)
    .map((block) => `<p>${escapeHtml(block).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

function plainTextToEditorHtml(value = '') {
  return escapeHtml(normalizeLineBreaks(value)).replace(/\n/g, '<br>');
}

function normalizeColor(value = '') {
  if (!canUseDom()) {
    return '';
  }

  const probe = document.createElement('span');
  probe.style.color = '';
  probe.style.color = String(value || '').trim();
  return probe.style.color || '';
}

function normalizeBackgroundColor(value = '') {
  if (!canUseDom()) {
    return '';
  }

  const probe = document.createElement('span');
  probe.style.backgroundColor = '';
  probe.style.backgroundColor = String(value || '').trim();
  return probe.style.backgroundColor || '';
}

function normalizeFontSize(value = '') {
  const numericValue = parseFloat(String(value || '').replace(/[^\d.]/g, ''));

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return '';
  }

  const clamped = Math.min(numericValue, 300);
  return `${clamped}px`;
}

function normalizeFontFamily(value = '') {
  if (!canUseDom()) {
    return '';
  }

  const probe = document.createElement('span');
  probe.style.fontFamily = '';
  probe.style.fontFamily = String(value || '').trim();
  return probe.style.fontFamily ? probe.style.fontFamily.replace(/\s*,\s*/g, ', ') : '';
}

function normalizeFontWeight(value = '') {
  const normalized = String(value || '').trim().toLowerCase();

  if (!normalized) {
    return '';
  }

  if (['normal', 'bold', 'bolder', 'lighter'].includes(normalized)) {
    return normalized;
  }

  if (/^[1-9]00$/.test(normalized)) {
    return normalized;
  }

  return '';
}

function normalizeFontStyle(value = '') {
  const normalized = String(value || '').trim().toLowerCase();

  if (['normal', 'italic', 'oblique'].includes(normalized)) {
    return normalized;
  }

  if (normalized.startsWith('oblique')) {
    return 'oblique';
  }

  return '';
}

function normalizeTextDecoration(value = '') {
  const normalized = String(value || '').trim().toLowerCase();

  if (!normalized) {
    return '';
  }

  const decorations = [];

  if (normalized.includes('underline')) {
    decorations.push('underline');
  }

  if (normalized.includes('line-through')) {
    decorations.push('line-through');
  }

  if (normalized.includes('overline')) {
    decorations.push('overline');
  }

  if (decorations.length > 0) {
    return decorations.join(' ');
  }

  return normalized.includes('none') ? 'none' : '';
}

function normalizeTextAlign(value = '') {
  const normalized = String(value || '').trim().toLowerCase();

  if (['left', 'right', 'center', 'justify'].includes(normalized)) {
    return normalized;
  }

  if (normalized === 'start') {
    return 'left';
  }

  if (normalized === 'end') {
    return 'right';
  }

  return '';
}

function sanitizeElementStyle(element) {
  const styles = [];
  const normalizedColor = normalizeColor(element.style.color || element.getAttribute('color'));
  const normalizedBackgroundColor = normalizeBackgroundColor(
    element.style.backgroundColor || element.getAttribute('bgcolor')
  );
  const legacyFontSize = LEGACY_FONT_SIZE_MAP[element.getAttribute('size')];
  const normalizedFontSize = normalizeFontSize(element.style.fontSize || legacyFontSize);
  const normalizedFontFamily = normalizeFontFamily(element.style.fontFamily || element.getAttribute('face'));
  const normalizedFontWeight = normalizeFontWeight(element.style.fontWeight);
  const normalizedFontStyle = normalizeFontStyle(element.style.fontStyle);
  const normalizedTextDecoration = normalizeTextDecoration(
    element.style.textDecoration || element.style.textDecorationLine
  );
  const normalizedTextAlign = normalizeTextAlign(element.style.textAlign || element.getAttribute('align'));

  if (normalizedColor) {
    styles.push(`color: ${normalizedColor}`);
  }

  if (normalizedBackgroundColor) {
    styles.push(`background-color: ${normalizedBackgroundColor}`);
  }

  if (normalizedFontSize) {
    styles.push(`font-size: ${normalizedFontSize}`);
  }

  if (normalizedFontFamily) {
    styles.push(`font-family: ${normalizedFontFamily}`);
  }

  if (normalizedFontWeight) {
    styles.push(`font-weight: ${normalizedFontWeight}`);
  }

  if (normalizedFontStyle) {
    styles.push(`font-style: ${normalizedFontStyle}`);
  }

  if (normalizedTextDecoration) {
    styles.push(`text-decoration: ${normalizedTextDecoration}`);
  }

  if (normalizedTextAlign) {
    styles.push(`text-align: ${normalizedTextAlign}`);
  }

  element.removeAttribute('style');
  element.removeAttribute('color');
  element.removeAttribute('size');
  element.removeAttribute('face');
  element.removeAttribute('align');

  if (styles.length > 0) {
    element.setAttribute('style', styles.join('; '));
  }
}

function unwrapElement(element) {
  const parent = element.parentNode;

  if (!parent) {
    return;
  }

  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element);
  }

  parent.removeChild(element);
}

function sanitizeElement(element) {
  Array.from(element.children).forEach(sanitizeElement);

  const tagName = element.tagName.toUpperCase();

  if (DROP_TAGS.has(tagName)) {
    element.remove();
    return;
  }

  if (tagName === 'FONT') {
    const replacement = element.ownerDocument.createElement('span');
    const normalizedColor = normalizeColor(element.getAttribute('color'));
    const normalizedBackgroundColor = normalizeBackgroundColor(
      element.style.backgroundColor || element.getAttribute('bgcolor')
    );
    const normalizedFontSize = normalizeFontSize(
      element.style.fontSize || LEGACY_FONT_SIZE_MAP[element.getAttribute('size')]
    );
    const normalizedFontFamily = normalizeFontFamily(element.style.fontFamily || element.getAttribute('face'));

    if (normalizedColor) {
      replacement.style.color = normalizedColor;
    }

    if (normalizedBackgroundColor) {
      replacement.style.backgroundColor = normalizedBackgroundColor;
    }

    if (normalizedFontSize) {
      replacement.style.fontSize = normalizedFontSize;
    }

    if (normalizedFontFamily) {
      replacement.style.fontFamily = normalizedFontFamily;
    }

    while (element.firstChild) {
      replacement.appendChild(element.firstChild);
    }

    element.replaceWith(replacement);
    sanitizeElement(replacement);
    return;
  }

  if (!ALLOWED_TAGS.has(tagName)) {
    unwrapElement(element);
    return;
  }

  Array.from(element.attributes).forEach((attribute) => {
    if (attribute.name.toLowerCase() !== 'style') {
      element.removeAttribute(attribute.name);
    }
  });

  if (tagName !== 'BR') {
    sanitizeElementStyle(element);
  } else {
    element.removeAttribute('style');
  }

  if (tagName === 'SPAN' && !element.getAttribute('style')) {
    unwrapElement(element);
  }
}

function toSafeDocumentHtml(value = '', wrapPlainText = true) {
  const normalized = normalizeLineBreaks(value);

  if (!normalized.trim()) {
    return '';
  }

  if (!containsHtml(normalized)) {
    return wrapPlainText ? plainTextToRichText(normalized) : plainTextToEditorHtml(normalized);
  }

  return normalized;
}

function cleanupEmptyHtml(value = '') {
  const sanitized = String(value || '').trim();

  if (!sanitized) {
    return '';
  }

  return getRichTextPlainText(sanitized).trim() ? sanitized : '';
}

export function sanitizeRichTextHtml(value = '', options = {}) {
  const { wrapPlainText = true } = options;
  const safeSource = toSafeDocumentHtml(value, wrapPlainText);

  if (!safeSource) {
    return '';
  }

  if (!canUseDom()) {
    return safeSource;
  }

  const parser = new DOMParser();
  const documentFragment = parser.parseFromString(`<div>${safeSource}</div>`, 'text/html');
  const root = documentFragment.body.firstElementChild;

  if (!root) {
    return '';
  }

  Array.from(root.children).forEach(sanitizeElement);
  root.normalize();

  return cleanupEmptyHtml(root.innerHTML);
}

export function hasRichTextEncodingIssues(value = '') {
  const normalized = normalizeLineBreaks(value);
  return normalized !== String(value ?? '').replace(/\r\n?/g, '\n');
}

export function getRichTextPlainText(value = '') {
  const normalized = normalizeLineBreaks(value);

  if (!normalized.trim()) {
    return '';
  }

  if (!containsHtml(normalized)) {
    return normalized;
  }

  if (!canUseDom()) {
    return normalized.replace(/<[^>]+>/g, ' ');
  }

  const parser = new DOMParser();
  const withBreaks = normalized
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|ul|ol)>/gi, '\n');
  const documentFragment = parser.parseFromString(`<div>${withBreaks}</div>`, 'text/html');
  const text = documentFragment.body.textContent || '';

  return text
    .replace(/\u00a0/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function getRichTextExcerpt(value = '', maxLength = 220) {
  const plainText = getRichTextPlainText(value).replace(/\s+/g, ' ').trim();

  if (!plainText) {
    return '';
  }

  if (plainText.length <= maxLength) {
    return plainText;
  }

  return `${plainText.slice(0, Math.max(0, maxLength - 1)).trimEnd()}...`;
}
